import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import api from '../utils/api';
import { 
    BugAntIcon, 
    CheckCircleIcon, 
    ExclamationTriangleIcon,
    MagnifyingGlassIcon,
    ClipboardDocumentListIcon
} from '@heroicons/react/24/outline';

function KargonomiDebugPanel() {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState(null);
    const [shipmentId, setShipmentId] = useState('');

    // Admin kontrolü
    const isAdmin = user?.app_metadata?.roles?.includes('admin') || user?.role === 'admin' || user?.email === 'hikibo06@gmail.com';
    
    if (!isAdmin) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <div className="flex items-center space-x-3">
                    <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
                    <div>
                        <h3 className="font-semibold text-red-800">Erişim Reddedildi</h3>
                        <p className="text-sm text-red-700">Bu panel sadece admin kullanıcılar için erişilebilir.</p>
                    </div>
                </div>
            </div>
        );
    }

    const runTest = async (testType, shipmentIdParam = null) => {
        setLoading(true);
        try {
            let endpoint = '';
            switch (testType) {
                case 'connection':
                    endpoint = '/api/debug-kargonomi/test-connection';
                    break;
                case 'shipment':
                    endpoint = `/api/debug-kargonomi/test-shipment/${shipmentIdParam}`;
                    break;
                case 'db-shipments':
                    endpoint = '/api/debug-kargonomi/db-shipments';
                    break;
                case 'tracking-logs':
                    endpoint = '/api/debug-kargonomi/tracking-logs';
                    break;
                default:
                    throw new Error('Geçersiz test tipi');
            }

            const response = await api.get(endpoint);
            setResults({
                type: testType,
                success: true,
                data: response
            });
            
            toast.success(`${testType} testi başarılı!`);
            
        } catch (error) {
            console.error(`${testType} test hatası:`, error);
            setResults({
                type: testType,
                success: false,
                error: error.message,
                details: error.response?.data || error.responseData
            });
            
            toast.error(`${testType} testi başarısız: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleShipmentTest = () => {
        if (!shipmentId.trim()) {
            toast.error('Lütfen Shipment ID girin');
            return;
        }
        runTest('shipment', shipmentId.trim());
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
                <div className="flex items-center space-x-3">
                    <BugAntIcon className="w-8 h-8 text-blue-600" />
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">Kargonomi API Debug Paneli</h2>
                        <p className="text-sm text-gray-600">
                            Kargonomi API bağlantısını ve otomatik takip sistemini test edin
                        </p>
                    </div>
                </div>
            </div>

            {/* Test Butonları */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* API Bağlantı Testi */}
                <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">API Bağlantı Testi</h3>
                    <p className="text-sm text-gray-600 mb-4">
                        Kargonomi API token'ı ve bağlantısını test eder
                    </p>
                    <button
                        onClick={() => runTest('connection')}
                        disabled={loading}
                        className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                        {loading ? 'Test Ediliyor...' : 'Bağlantıyı Test Et'}
                    </button>
                </div>

                {/* Veritabanı Shipment'ları */}
                <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">DB Shipment'ları</h3>
                    <p className="text-sm text-gray-600 mb-4">
                        Veritabanındaki kargo kayıtlarını listeler
                    </p>
                    <button
                        onClick={() => runTest('db-shipments')}
                        disabled={loading}
                        className="w-full bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
                    >
                        {loading ? 'Listeleniyor...' : 'DB Shipment\'ları Listele'}
                    </button>
                </div>

                {/* Takip Logları */}
                <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Takip Logları</h3>
                    <p className="text-sm text-gray-600 mb-4">
                        Otomatik takip sistem loglarını gösterir
                    </p>
                    <button
                        onClick={() => runTest('tracking-logs')}
                        disabled={loading}
                        className="w-full bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50"
                    >
                        {loading ? 'Yükleniyor...' : 'Takip Loglarını Göster'}
                    </button>
                </div>

                {/* Belirli Shipment Testi */}
                <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Shipment ID Testi</h3>
                    <p className="text-sm text-gray-600 mb-4">
                        Belirli bir Kargonomi Shipment ID'yi test eder
                    </p>
                    <div className="space-y-3">
                        <input
                            type="text"
                            value={shipmentId}
                            onChange={(e) => setShipmentId(e.target.value)}
                            placeholder="Kargonomi Shipment ID girin"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                            onClick={handleShipmentTest}
                            disabled={loading || !shipmentId.trim()}
                            className="w-full bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 disabled:opacity-50"
                        >
                            {loading ? 'Test Ediliyor...' : 'Shipment\'ı Test Et'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Test Sonuçları */}
            {results && (
                <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
                    <div className="flex items-center space-x-3 mb-4">
                        {results.success ? (
                            <CheckCircleIcon className="w-6 h-6 text-green-600" />
                        ) : (
                            <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
                        )}
                        <h3 className="text-lg font-semibold text-gray-900">
                            Test Sonucu: {results.type}
                        </h3>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4 overflow-auto max-h-96">
                        <pre className="text-sm text-gray-800 whitespace-pre-wrap">
                            {JSON.stringify(results, null, 2)}
                        </pre>
                    </div>
                </div>
            )}
        </div>
    );
}

export default KargonomiDebugPanel;